import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Notification from '../components/common/Notification';
import ConfirmModal from '../components/common/ConfirmModal';
import PersianDatePicker from '../components/PersianDatePicker';
import TimePicker from '../components/TimePicker';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { AppointmentAmiCall, CallLog } from '../types';

interface Appointment {
  id: number;
  user_id: number;
  service_id?: number | null;
  status: string;
  step_completed: number;
  prescription_file_path?: string;
  prescription_tracking_code?: string;
  tracking_code?: string;
  appointment_date?: string;
  appointment_time?: string;
  cancel_reason?: string;
  cancelled_at?: string;
  status_before_cancel?: string;
  can_restore_from_cancel?: boolean;
  restore_from_cancel_deadline?: string;
  created_at: string;
  updated_at?: string;
  user?: {
    full_name: string;
    phone: string;
    national_id?: string;
  };
  service_ids?: number[];
  service?: {
    name: string;
    price?: number;
    description?: string;
  };
  services?: {
    id?: number;
    name: string;
    price?: number;
    description?: string;
  }[];
  files?: {
    id: number;
    file_path: string;
    file_type?: string;
    created_at: string;
  }[];
  upload_token_expires_at?: string;
}

interface AdminServiceOption {
  id: number;
  name: string;
  is_active: boolean;
}

function formatCallDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins <= 0) return `${secs} ثانیه`;
  return `${mins} دقیقه و ${secs} ثانیه`;
}

function formatCallDateParts(value: string): { dateLabel: string; timeLabel: string } {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { dateLabel: '—', timeLabel: '—' };
  }
  return {
    dateLabel: parsed.toLocaleDateString('fa-IR', { year: 'numeric', month: 'short', day: 'numeric' }),
    timeLabel: parsed.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  };
}

function getCallStatusVisuals(connected: boolean) {
  if (connected) {
    return {
      stripe: 'bg-emerald-500',
      iconWrap: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
      cardBorder: 'border-emerald-100/90',
      progressBar: 'bg-emerald-500',
    };
  }
  return {
    stripe: 'bg-rose-500',
    iconWrap: 'bg-rose-50 text-rose-600 ring-rose-100',
    cardBorder: 'border-rose-100/90',
    progressBar: 'bg-rose-500',
  };
}

function InboundCallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7L7 17M7 17H15M7 17V9" />
    </svg>
  );
}

function OutboundCallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H9M17 7V15" />
    </svg>
  );
}

function PhoneCallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

type CallHistoryEntry =
  | { kind: 'manual'; sortAt: number; data: CallLog }
  | { kind: 'ami'; sortAt: number; data: AppointmentAmiCall };

const AdminAppointmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'info' | 'warning' | 'error' } | null>(null);
  const detailLockActiveRef = useRef(false);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [amiCalls, setAmiCalls] = useState<AppointmentAmiCall[]>([]);
  const [callLogsLoading, setCallLogsLoading] = useState(false);
  const [savingCallLog, setSavingCallLog] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [isCallPanelOpen, setIsCallPanelOpen] = useState(false);
  const [hasActionInSession, setHasActionInSession] = useState(false);

  const [operatorServices, setOperatorServices] = useState<AdminServiceOption[]>([]);
  const [operatorServicesLoading, setOperatorServicesLoading] = useState(false);
  const [operatorNationalId, setOperatorNationalId] = useState('');
  const [operatorServiceId, setOperatorServiceId] = useState('');
  const [operatorTrackingCode, setOperatorTrackingCode] = useState('');
  const [savingOperatorFields, setSavingOperatorFields] = useState(false);
  const operatorServerSnapshotRef = useRef<string | null>(null);

  // State Tabs
  const [activeTab, setActiveTab] = useState<'user_info' | 'appointment_info' | 'service_info' | 'prescription_info' | 'files_info'>('user_info');

  // State برای پاپ‌آپ‌ها
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');
  /** نوبت «بین مریض» — متن اضافهٔ راهنما به پیامک زمان ارسال می‌شود */
  const [betweenPatients, setBetweenPatients] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [sendCancelSms, setSendCancelSms] = useState(true);
  const [sendCompleteSms, setSendCompleteSms] = useState(true);
  const [showDeletePermanentModal, setShowDeletePermanentModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoringFromCancel, setRestoringFromCancel] = useState(false);
  const [deletingPermanent, setDeletingPermanent] = useState(false);
  /**
   * نقش فعلی: ابتدا از localStorage (همان چیزی که لاگین ست کرده) تا دکمهٔ حذف دیده شود؛
   * بلافاصله با پاسخ /auth/me از سرور هم‌راستا می‌شود.
   */
  const [viewerRole, setViewerRole] = useState<string | null>(() => {
    try {
      return localStorage.getItem('admin_role');
    } catch {
      return null;
    }
  });

  const getErrorMessage = useCallback(async (response: Response, fallback: string) => {
    try {
      const data = await response.json();
      if (typeof data?.detail === 'string' && data.detail.trim()) {
        return data.detail;
      }
    } catch {
      // ignore parse error
    }
    return fallback;
  }, []);

  const fetchAppointmentDetail = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/appointments/${id}`, {

        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'خطا در دریافت جزئیات نوبت'));
      }

      const data = await response.json();
      setAppointment(data);
      detailLockActiveRef.current = true;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, getErrorMessage]);

  const fetchOperatorServices = useCallback(async () => {
    try {
      setOperatorServicesLoading(true);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/services`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'خطا در دریافت لیست خدمات'));
      }
      const data = await response.json();
      setOperatorServices(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setNotification({ message: err.message || 'خطا در دریافت لیست خدمات', type: 'error' });
    } finally {
      setOperatorServicesLoading(false);
    }
  }, [getErrorMessage]);

  const fetchCallLogs = useCallback(async () => {
    if (!id) return;
    try {
      setCallLogsLoading(true);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/appointments/${id}/call-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'خطا در دریافت تاریخچه تماس'));
      }

      const data = await response.json();
      setCallLogs(Array.isArray(data?.manual_logs) ? data.manual_logs : []);
      setAmiCalls(Array.isArray(data?.ami_calls) ? data.ami_calls : []);
    } catch {
      setCallLogs([]);
      setAmiCalls([]);
    } finally {
      setCallLogsLoading(false);
    }
  }, [id, getErrorMessage]);

  const callHistoryEntries = React.useMemo<CallHistoryEntry[]>(() => {
    const manualEntries: CallHistoryEntry[] = callLogs.map((log) => ({
      kind: 'manual',
      sortAt: new Date(log.created_at).getTime(),
      data: log,
    }));
    const amiEntries: CallHistoryEntry[] = amiCalls.map((call) => ({
      kind: 'ami',
      sortAt: new Date(call.call_time).getTime() || 0,
      data: call,
    }));
    return [...manualEntries, ...amiEntries].sort((a, b) => b.sortAt - a.sortAt);
  }, [callLogs, amiCalls]);

  const totalCallHistoryCount = callHistoryEntries.length;

  useEffect(() => {
    if (!appointment) {
      operatorServerSnapshotRef.current = null;
      return;
    }
    if (appointment.status !== 'pending') {
      operatorServerSnapshotRef.current = null;
      return;
    }
    const missingNational = !((appointment.user?.national_id || '').trim());
    const missingService = appointment.service_id == null;
    const missingTrack = !((appointment.prescription_tracking_code || '').trim());
    if (!missingNational && !missingService && !missingTrack) {
      operatorServerSnapshotRef.current = null;
      return;
    }

    const snapshot = [
      (appointment.user?.national_id || '').trim(),
      appointment.service_id != null ? String(appointment.service_id) : '',
      (appointment.prescription_tracking_code || '').trim(),
    ].join('|');

    if (operatorServerSnapshotRef.current === snapshot) {
      return;
    }
    operatorServerSnapshotRef.current = snapshot;

    setOperatorNationalId((appointment.user?.national_id || '').trim());
    setOperatorServiceId(appointment.service_id != null ? String(appointment.service_id) : '');
    setOperatorTrackingCode((appointment.prescription_tracking_code || '').trim());
  }, [appointment]);

  useEffect(() => {
    if (!appointment || appointment.status !== 'pending') return;
    if (operatorServices.length > 0 || operatorServicesLoading) return;
    void fetchOperatorServices();
  }, [appointment, operatorServices.length, operatorServicesLoading, fetchOperatorServices]);

  const saveOperatorFields = async () => {
    if (!id || !appointment || savingOperatorFields) return;
    if (appointment.status !== 'pending') {
      setNotification({ message: 'فقط برای نوبت در انتظار قابل ذخیره است', type: 'warning' });
      return;
    }

    const payload: {
      national_id?: string;
      service_id?: number;
      prescription_tracking_code?: string;
    } = {};

    const nid = operatorNationalId.trim();
    const currentNid = (appointment.user?.national_id || '').trim();
    if (nid !== currentNid) {
      if (!nid) {
        setNotification({ message: 'کد ملی را وارد کنید', type: 'warning' });
        return;
      }
      if (nid.length !== 10 || !/^\d{10}$/.test(nid)) {
        setNotification({ message: 'کد ملی باید ۱۰ رقم باشد', type: 'warning' });
        return;
      }
      payload.national_id = nid;
    }

    const sidStr = operatorServiceId.trim();
    const sid = sidStr ? parseInt(sidStr, 10) : NaN;
    const currentSid = appointment.service_id ?? null;
    if (sidStr) {
      if (Number.isNaN(sid) || sid <= 0) {
        setNotification({ message: 'نوع خدمت را انتخاب کنید', type: 'warning' });
        return;
      }
      if (sid !== currentSid) {
        payload.service_id = sid;
      }
    }

    const track = operatorTrackingCode.trim();
    const currentTrack = (appointment.prescription_tracking_code || '').trim();
    if (track !== currentTrack) {
      if (!track) {
        setNotification({ message: 'کد رهگیری نسخه را وارد کنید', type: 'warning' });
        return;
      }
      payload.prescription_tracking_code = track;
    }

    if (Object.keys(payload).length === 0) {
      setNotification({ message: 'تغییری برای ذخیره وجود ندارد', type: 'info' });
      return;
    }

    try {
      setSavingOperatorFields(true);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/appointments/${id}/operator-fields`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'خطا در ذخیره اطلاعات'));
      }
      const data = await response.json();
      setAppointment(data);
      setHasActionInSession(true);
      setNotification({ message: 'اطلاعات با موفقیت ذخیره شد', type: 'success' });
    } catch (err: any) {
      setNotification({ message: err.message || 'خطا در ذخیره اطلاعات', type: 'error' });
    } finally {
      setSavingOperatorFields(false);
    }
  };

  const submitCallLog = async () => {
    if (!id || savingCallLog) return;
    try {
      setSavingCallLog(true);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/appointments/${id}/call-logs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          outcome: 'no_answer',
          notes: callNotes.trim() || null,
        }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'خطا در ثبت تلاش تماس'));
      }

      setCallNotes('');
      await fetchCallLogs();
      setHasActionInSession(true);
      setNotification({ message: 'تاریخچه ثبت شد', type: 'success' });
    } catch (err: any) {
      setNotification({ message: err.message || 'خطا در ثبت تلاش تماس', type: 'error' });
    } finally {
      setSavingCallLog(false);
    }
  };

  const confirmExitIfNoAction = useCallback(() => {
    if (hasActionInSession) return true;
    return window.confirm('هنوز اقدامی روی این نوبت ثبت نشده. مطمئنید می‌خواهید خارج شوید؟');
  }, [hasActionInSession]);

  const handleBackToDashboard = useCallback(() => {
    if (!confirmExitIfNoAction()) return;
    navigate('/admin/dashboard');
  }, [confirmExitIfNoAction, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const apiBaseUrl = getApiBaseUrl();
    const load = async () => {
      try {
        const meRes = await fetch(`${apiBaseUrl}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (meRes.ok) {
          const me = await meRes.json();
          const serverRole = me.role != null ? String(me.role).trim() : null;
          setViewerRole(serverRole);
          if (serverRole) {
            try {
              localStorage.setItem('admin_role', serverRole);
            } catch {
              /* ignore */
            }
          }
        }
        /* اگر /auth/me خطا داد، همان نقش اولیهٔ localStorage دست‌نخورده می‌ماند */
      } catch {
        /* ignore */
      }
      if (id) {
        await fetchAppointmentDetail();
        await fetchCallLogs();
      }
    };

    void load();
  }, [navigate, id, fetchAppointmentDetail, fetchCallLogs]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasActionInSession) return;
      const message = 'هنوز اقدامی روی این نوبت ثبت نشده است.';
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasActionInSession]);

  useEffect(() => {
    const historyObj = window.history as History & {
      pushState: (data: unknown, unused: string, url?: string | URL | null) => void;
      replaceState: (data: unknown, unused: string, url?: string | URL | null) => void;
    };

    const originalPushState = historyObj.pushState.bind(historyObj);
    const originalReplaceState = historyObj.replaceState.bind(historyObj);

    const canNavigateTo = (url?: string | URL | null) => {
      if (!url) return true;
      try {
        const next = new URL(String(url), window.location.origin);
        const current = window.location;
        const isSameRoute =
          next.pathname === current.pathname &&
          next.search === current.search &&
          next.hash === current.hash;
        if (isSameRoute) return true;
      } catch {
        return true;
      }
      return confirmExitIfNoAction();
    };

    historyObj.pushState = ((data: unknown, unused: string, url?: string | URL | null) => {
      if (!canNavigateTo(url)) return;
      originalPushState(data, unused, url);
    }) as typeof historyObj.pushState;

    historyObj.replaceState = ((data: unknown, unused: string, url?: string | URL | null) => {
      if (!canNavigateTo(url)) return;
      originalReplaceState(data, unused, url);
    }) as typeof historyObj.replaceState;

    return () => {
      historyObj.pushState = originalPushState;
      historyObj.replaceState = originalReplaceState;
    };
  }, [confirmExitIfNoAction]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    if (!token) {
      return;
    }

    const apiBaseUrl = getApiBaseUrl();
    const heartbeatId = setInterval(async () => {
      if (!detailLockActiveRef.current) {
        return;
      }

      try {
        const response = await fetch(`${apiBaseUrl}/admin/appointments/${id}/lock`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errMsg = await getErrorMessage(response, 'قفل این نوبت از دسترس خارج شد');
          setError(errMsg);
          detailLockActiveRef.current = false;
        }
      } catch {
        // در خطای شبکه، قفل محلی را نگه می‌داریم تا تلاش بعدی انجام شود
      }
    }, 30000);

    return () => {
      clearInterval(heartbeatId);
    };
  }, [id, getErrorMessage]);

  useEffect(() => {
    return () => {
      if (!detailLockActiveRef.current || !id) {
        return;
      }
      const token = localStorage.getItem('admin_token');
      if (!token) {
        return;
      }
      void fetch(`${getApiBaseUrl()}/admin/appointments/${id}/lock`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        keepalive: true,
      });
    };
  }, [id]);

  // Polling for file updates when on the files tab
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    // Check if token is valid
    const isTokenValid = appointment?.upload_token_expires_at && new Date(appointment.upload_token_expires_at).getTime() > new Date().getTime();

    if (activeTab === 'files_info' && id && isTokenValid) {
      intervalId = setInterval(() => {
        // Double check validity inside interval
        if (appointment?.upload_token_expires_at && new Date(appointment.upload_token_expires_at).getTime() > new Date().getTime()) {
          fetchAppointmentDetail();
        } else {
          clearInterval(intervalId);
        }
      }, 5000); // هر 5 ثانیه رفرش کن
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeTab, id, fetchAppointmentDetail, appointment?.upload_token_expires_at]);

  // Timer update effect (force re-render every second to update countdown UI)
  useEffect(() => {
    if (activeTab === 'files_info' && appointment?.upload_token_expires_at) {
      const interval = setInterval(() => {
        // This just forces a re-render for the timer display
        // We can use a dummy state update or just rely on the component re-rendering
        // But to be clean, let's just let React handle it if we put the calculation in the render
        // We need a state trigger.
        setAppointment(prev => prev ? { ...prev } : null);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [activeTab, appointment?.upload_token_expires_at]);


  // تابع‌های پاپ‌آپ
  const openTimeModal = () => {
    setShowTimeModal(true);
  };

  const closeTimeModal = () => {
    setShowTimeModal(false);
    setAppointmentDate('');
    setAppointmentTime('');
    setAppointmentNotes('');
    setBetweenPatients(false);
    setSendCompleteSms(true);
  };

  const openCancelModal = () => {
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancelReason('');
    setSendCancelSms(true);
  };

  const saveAppointmentTime = async () => {
    if (!appointmentDate || !appointmentTime) {
      setNotification({ message: 'لطفاً تاریخ و زمان را وارد کنید', type: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();

      // تکمیل نوبت - تنظیم زمان و تغییر وضعیت در یک درخواست
      const response = await fetch(`${apiBaseUrl}/admin/appointments/${id}/complete`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appointment_date: appointmentDate,
          appointment_time: appointmentTime,
          notes: appointmentNotes,
          between_patients: betweenPatients,
          send_sms: sendCompleteSms,
        }),
      });

      if (!response.ok) {
        throw new Error('خطا در تکمیل نوبت');
      }

      closeTimeModal();
      fetchAppointmentDetail();
      setHasActionInSession(true);
      setNotification({ message: 'نوبت با موفقیت تکمیل شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در تکمیل نوبت', type: 'error' });
    }
  };

  const cancelAppointment = async () => {
    if (!cancelReason.trim()) {
      setNotification({ message: 'لطفاً دلیل لغو را وارد کنید', type: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();

      const response = await fetch(`${apiBaseUrl}/admin/appointments/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'cancelled',
          cancel_reason: cancelReason,
          send_sms: sendCancelSms,
        }),
      });

      if (!response.ok) {
        throw new Error('خطا در لغو نوبت');
      }

      closeCancelModal();
      fetchAppointmentDetail();
      setHasActionInSession(true);
      setNotification({ message: 'نوبت با موفقیت لغو شد', type: 'success' });
    } catch (err: any) {
      setError(err.message);
      setNotification({ message: err.message || 'خطا در لغو نوبت', type: 'error' });
    }
  };

  const restoreFromCancel = async () => {
    if (!id) return;
    try {
      setRestoringFromCancel(true);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/appointments/${id}/restore-from-cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'خطا در بازگردانی نوبت'));
      }

      setShowRestoreModal(false);
      fetchAppointmentDetail();
      setHasActionInSession(true);
      setNotification({ message: 'نوبت با موفقیت از لغو بازگردانی شد', type: 'success' });
    } catch (err: any) {
      setNotification({ message: err.message || 'خطا در بازگردانی نوبت', type: 'error' });
    } finally {
      setRestoringFromCancel(false);
    }
  };

  const confirmPermanentDelete = async () => {
    if (deletingPermanent || !id) return;
    try {
      setDeletingPermanent(true);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/admin/appointments/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        let msg = 'حذف انجام نشد';
        try {
          const errBody = await res.json();
          if (errBody.detail) {
            msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      setShowDeletePermanentModal(false);
      setHasActionInSession(true);
      sessionStorage.setItem('appointment_deleted_flash', 'نوبت به طور کامل از سیستم حذف شد.');
      navigate('/admin/dashboard');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'خطا در حذف';
      setNotification({ message: msg, type: 'error' });
    } finally {
      setDeletingPermanent(false);
    }
  };

  const isSuperAdmin =
    (viewerRole != null && String(viewerRole).trim().toLowerCase() === 'admin');
  const isLockConflictError = Boolean(error && /داخل این نوبت است/.test(error));

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'در انتظار' },
      in_progress: { color: 'bg-blue-100 text-blue-800', text: 'در حال انجام' },
      completed: { color: 'bg-green-100 text-green-800', text: 'تکمیل شده' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'لغو شده' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner text="در حال بارگذاری جزئیات نوبت..." />
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="p-8">
        <div className={`${isLockConflictError ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'} border rounded-xl p-6 shadow-sm`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {isLockConflictError ? (
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 8a6 6 0 10-12 0v1H5a1 1 0 00-1 1v7a1 1 0 001 1h8a1 1 0 001-1v-7a1 1 0 00-1-1h-1V8a4 4 0 10-8 0v1h2V8a2 2 0 114 0v1h6V8z" clipRule="evenodd" />
                  </svg>
                </div>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-bold ${isLockConflictError ? 'text-amber-800' : 'text-red-800'}`}>
                {isLockConflictError ? 'این نوبت در حال بررسی است' : 'خطا'}
              </h3>
              <div className={`mt-2 text-sm leading-6 ${isLockConflictError ? 'text-amber-700' : 'text-red-700'}`}>
                <p>{error || 'نوبت یافت نشد'}</p>
              </div>
              {isLockConflictError && (
                <div className="mt-3 text-xs text-amber-700 bg-amber-100 rounded-lg px-3 py-2 border border-amber-200">
                  پس از خروج کارشناس فعلی یا پایان زمان قفل، دوباره وارد جزئیات همین نوبت شوید.
                </div>
              )}
              <div className="mt-4">
                <button
                  onClick={handleBackToDashboard}
                  className={`${isLockConflictError ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-red-100 hover:bg-red-200 text-red-800'} px-4 py-2 rounded-lg text-sm font-medium transition-colors`}
                >
                  بازگشت به لیست نوبت‌ها
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const attachmentFileCount =
    appointment.files && appointment.files.length > 0
      ? appointment.files.length
      : appointment.prescription_file_path
        ? 1
        : 0;
  const attachmentFileCountLabel = attachmentFileCount.toLocaleString('fa-IR');

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <ConfirmModal
        isOpen={showRestoreModal}
        title="بازگردانی از لغو"
        message="نوبت به وضعیت قبل از لغو برمی‌گردد و می‌توانید دوباره آن را نهایی کنید. ادامه می‌دهید؟"
        onConfirm={() => void restoreFromCancel()}
        onCancel={() => {
          if (!restoringFromCancel) setShowRestoreModal(false);
        }}
        confirmText={restoringFromCancel ? 'در حال بازگردانی...' : 'بازگردانی'}
        cancelText="انصراف"
        confirmButtonClass="bg-amber-600 hover:bg-amber-700"
      />

      <ConfirmModal
        isOpen={showDeletePermanentModal}
        title="حذف دائمی نوبت"
        message="این نوبت از دیتابیس و فایل‌های پیوست (نسخه، مدارک و غیره) به‌طور کامل پاک می‌شود و قابل بازگشت نیست. آیا مطمئن هستید؟"
        onConfirm={() => void confirmPermanentDelete()}
        onCancel={() => {
          if (!deletingPermanent) setShowDeletePermanentModal(false);
        }}
        confirmText={deletingPermanent ? 'در حال حذف...' : 'حذف کامل'}
        cancelText="انصراف"
        confirmButtonClass="bg-gray-900 hover:bg-black"
      />

      <button
        type="button"
        onClick={() => setIsCallPanelOpen(true)}
        className="fixed left-4 bottom-4 z-40 bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
      >
        تاریخچه تماس
        <span className="inline-flex items-center justify-center min-w-6 h-6 px-1 rounded-full bg-white text-indigo-700 text-xs font-bold">
          {totalCallHistoryCount}
        </span>
      </button>

      <div
        className={`fixed inset-0 z-40 transition-opacity ${isCallPanelOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!isCallPanelOpen}
      >
        <div
          className={`absolute inset-0 bg-black/30 transition-opacity duration-200 ${isCallPanelOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsCallPanelOpen(false)}
        />
        <aside
          className={`absolute left-0 top-0 h-full w-[360px] max-w-[92vw] bg-white shadow-2xl border-r border-gray-200 transition-transform duration-200 ${
            isCallPanelOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-4 border-b border-gray-100 bg-gradient-to-l from-indigo-50/80 to-white flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-900">تاریخچه تماس</h2>
              {totalCallHistoryCount > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">{totalCallHistoryCount} مورد ثبت‌شده</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsCallPanelOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3 space-y-2">
              <input
                type="text"
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                placeholder="یادداشت تماس (اختیاری)"
                className="w-full px-3 py-2.5 border border-white bg-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => void submitCallLog()}
                  disabled={savingCallLog}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 shadow-sm"
                >
                  {savingCallLog ? 'در حال ثبت...' : 'ثبت یادداشت'}
                </button>
              </div>
            </div>

            <div className="max-h-[calc(100vh-240px)] overflow-auto rounded-2xl border border-slate-200/80 bg-gradient-to-b from-slate-50 to-white p-2.5">
              {callLogsLoading ? (
                <div className="p-6 text-center text-sm text-gray-500">در حال بارگذاری...</div>
              ) : callHistoryEntries.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                    <PhoneCallIcon />
                  </div>
                  <p className="text-sm font-medium text-slate-600">تماسی یافت نشد</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {callHistoryEntries.slice(0, 30).map((entry) => (
                    entry.kind === 'manual' ? (
                      (() => {
                        const { dateLabel, timeLabel } = formatCallDateParts(entry.data.created_at);
                        return (
                          <li
                            key={`manual-${entry.data.id}`}
                            className="rounded-xl border border-dashed border-amber-200 bg-amber-50/60 px-3.5 py-3 shadow-sm"
                          >
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  یادداشت
                                </span>
                                <span className="text-xs font-medium text-amber-900/80 truncate">
                                  {entry.data.admin_username}
                                </span>
                              </div>
                              <div className="text-left shrink-0">
                                <p className="text-[11px] font-medium text-amber-900/70 tabular-nums">{timeLabel}</p>
                                <p className="text-[10px] text-amber-800/50 tabular-nums">{dateLabel}</p>
                              </div>
                            </div>
                            <p className="text-sm text-slate-800 leading-6 whitespace-pre-wrap break-words">
                              {entry.data.notes?.trim() || '—'}
                            </p>
                          </li>
                        );
                      })()
                    ) : (
                      (() => {
                        const call = entry.data;
                        const visuals = getCallStatusVisuals(call.answered);
                        const { dateLabel, timeLabel } = formatCallDateParts(call.call_time);
                        const DirectionIcon =
                          call.direction === 'inbound'
                            ? InboundCallIcon
                            : call.direction === 'outbound'
                              ? OutboundCallIcon
                              : PhoneCallIcon;
                        const durationPct = Math.min(100, Math.round((call.duration_seconds / 300) * 100));
                        return (
                          <li
                            key={`ami-${call.unique_id || call.call_time}`}
                            className={`relative overflow-hidden rounded-2xl border bg-white shadow-sm hover:shadow-md transition-shadow ${visuals.cardBorder}`}
                          >
                            <div className={`absolute right-0 top-0 bottom-0 w-1 ${visuals.stripe}`} />
                            <div className="p-3.5 pr-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2.5 min-w-0">
                                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${visuals.iconWrap}`}>
                                    <DirectionIcon />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-bold text-slate-900 truncate">{call.agent_label}</p>
                                    <p className="text-[11px] font-medium mt-0.5 text-slate-500">
                                      {call.direction_label} · مرکز تلفن
                                    </p>
                                  </div>
                                </div>
                                <div className="text-left shrink-0">
                                  <p className="text-xs font-semibold text-slate-700 tabular-nums">{timeLabel}</p>
                                  <p className="text-[10px] text-slate-400 tabular-nums">{dateLabel}</p>
                                </div>
                              </div>

                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ${
                                    call.answered
                                      ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                                      : 'bg-rose-50 text-rose-700 ring-rose-100'
                                  }`}
                                >
                                  {call.disposition_label}
                                </span>
                                <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                                  {call.direction_label}
                                </span>
                              </div>

                              {call.answered && (
                                <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                                  <div className="flex items-center justify-between gap-2 text-xs text-slate-600">
                                    <span className="inline-flex items-center gap-1.5">
                                      <svg className="h-3.5 w-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      مدت مکالمه
                                    </span>
                                    <span className="font-semibold text-slate-800 tabular-nums">
                                      {formatCallDuration(call.duration_seconds)}
                                    </span>
                                  </div>
                                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                                    <div
                                      className={`h-full rounded-full transition-all ${visuals.progressBar}`}
                                      style={{ width: `${Math.max(durationPct, 8)}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })()
                    )
                  ))}
                </ul>
              )}
            </div>
          </div>
        </aside>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">جزئیات نوبت</h1>
              <p className="mt-2 text-gray-600">شماره نوبت: {appointment.tracking_code || (1000 + appointment.id)}</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={() => setShowDeletePermanentModal(true)}
                  className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  حذف دائمی
                </button>
              )}
              <button
                onClick={openCancelModal}
                disabled={appointment.status === 'cancelled'}
                className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors border border-red-200 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                لغو نوبت
              </button>
              {appointment.can_restore_from_cancel && (
                <button
                  type="button"
                  onClick={() => setShowRestoreModal(true)}
                  className="bg-amber-50 text-amber-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors border border-amber-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  بازگردانی از لغو
                </button>
              )}
              {(appointment.status === 'in_progress' || appointment.status === 'pending') && (
                <button
                  onClick={openTimeModal}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors shadow-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  تکمیل نوبت
                </button>
              )}
              <button
                onClick={handleBackToDashboard}
                className="bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 border border-gray-300 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                بازگشت به لیست
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('user_info')}
                className={`${activeTab === 'user_info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors`}
              >
                اطلاعات کاربر
              </button>
              <button
                onClick={() => setActiveTab('appointment_info')}
                className={`${activeTab === 'appointment_info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors`}
              >
                اطلاعات نوبت
              </button>
              <button
                onClick={() => setActiveTab('service_info')}
                className={`${activeTab === 'service_info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors`}
              >
                اطلاعات خدمت
              </button>
              <button
                onClick={() => setActiveTab('prescription_info')}
                className={`${activeTab === 'prescription_info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors`}
              >
                نسخه پزشکی
              </button>
              <button
                onClick={() => setActiveTab('files_info')}
                className={`${activeTab === 'files_info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } w-1/5 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors inline-flex items-center justify-center gap-1.5`}
              >
                <span>فایل‌ها</span>
                <span
                  className={`inline-flex min-h-[1.375rem] min-w-[1.375rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums leading-none ${
                    activeTab === 'files_info'
                      ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-200/80'
                      : attachmentFileCount > 0
                        ? 'bg-slate-200 text-slate-800 ring-1 ring-slate-300/60'
                        : 'bg-gray-100 text-gray-400 ring-1 ring-gray-200'
                  }`}
                  dir="ltr"
                  aria-label={`تعداد فایل: ${attachmentFileCount}`}
                >
                  {attachmentFileCountLabel}
                </span>
              </button>
            </nav>
          </div>

          <div className="p-6 min-h-[300px]">
            {/* اطلاعات کاربر */}
            {activeTab === 'user_info' && (
              <div className="w-full">
                <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-inner">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3 border-b border-gray-200 pb-4">
                    <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    مشخصات بیمار
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                    <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center">
                      <span className="text-sm font-medium text-gray-500 mb-1">نام و نام خانوادگی</span>
                      <span className="text-lg lg:text-xl font-bold text-gray-900">{appointment.user?.full_name || '---'}</span>
                    </div>

                    <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center">
                      <span className="text-sm font-medium text-gray-500 mb-1">شماره تماس</span>
                      <span className="text-lg lg:text-xl font-bold text-blue-700 font-mono text-left" dir="ltr">{appointment.user?.phone || '---'}</span>
                    </div>

                    <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center">
                      <span className="text-sm font-medium text-gray-500 mb-1">کد ملی</span>
                      {appointment.status === 'pending' ? (
                        <div className="mt-1 space-y-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={10}
                            value={operatorNationalId}
                            onChange={(e) => setOperatorNationalId(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-base font-mono text-left"
                            dir="ltr"
                            placeholder="۱۰ رقم"
                          />
                          <button
                            type="button"
                            onClick={() => void saveOperatorFields()}
                            disabled={savingOperatorFields}
                            className="inline-flex rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            {savingOperatorFields ? '…' : 'ذخیره کد ملی'}
                          </button>
                        </div>
                      ) : (
                        <span className="text-lg lg:text-xl font-bold text-gray-900 font-mono tracking-wider text-left" dir="ltr">{appointment.user?.national_id || 'ثبت نشده'}</span>
                      )}
                    </div>

                    <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center">
                      <span className="text-sm font-medium text-gray-500 mb-1">تاریخ ثبت نوبت</span>
                      <span className="text-lg font-bold text-gray-900 text-left" dir="ltr">{new Date(appointment.created_at).toLocaleString('fa-IR')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* اطلاعات نوبت */}
            {activeTab === 'appointment_info' && (
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6 border-b pb-2">
                  اطلاعات نوبت
                </h3>

                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">وضعیت فعلی</dt>
                    <dd className="mt-1">
                      {getStatusBadge(appointment.status)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">شماره پیگیری نوبت</dt>
                    <dd className="mt-1 text-lg font-bold text-gray-900 font-mono" dir="ltr">
                      {appointment.tracking_code || 'ثبت نشده'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">مرحله تکمیل</dt>
                    <dd className="mt-1">
                      {appointment.step_completed === 2 ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          تکمیل شده (۲ از ۲)
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          مرحله {appointment.step_completed} از ۲
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">تاریخ نوبت تعیین شده</dt>
                    <dd className="mt-1 text-lg font-semibold text-blue-700">
                      {appointment.appointment_date || 'هنوز تعیین نشده'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">زمان نوبت تعیین شده</dt>
                    <dd className="mt-1 text-lg font-semibold text-blue-700">
                      {appointment.appointment_time || 'هنوز تعیین نشده'}
                    </dd>
                  </div>

                  {appointment.status === 'cancelled' && appointment.cancel_reason && (
                    <div className="sm:col-span-2 mt-4">
                      <dt className="text-sm font-medium text-red-600 mb-1">دلیل لغو نوبت</dt>
                      <dd className="text-sm text-gray-900 bg-red-50 p-4 rounded-lg border border-red-200">
                        {appointment.cancel_reason}
                      </dd>
                    </div>
                  )}
                  {appointment.status === 'cancelled' && appointment.can_restore_from_cancel && appointment.restore_from_cancel_deadline && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-amber-700 mb-1">مهلت بازگردانی از لغو</dt>
                      <dd className="text-sm text-amber-800 bg-amber-50 p-3 rounded-lg border border-amber-200">
                        تا{' '}
                        {new Date(appointment.restore_from_cancel_deadline).toLocaleString('fa-IR', {
                          timeZone: 'Asia/Tehran',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        امکان بازگردانی این نوبت وجود دارد.
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* اطلاعات خدمت */}
            {activeTab === 'service_info' && (
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6 border-b pb-2">
                  اطلاعات خدمات انتخابی
                </h3>
                {appointment.status === 'pending' && (
                  <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50/80 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">نوع خدمت</label>
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2 max-w-xl">
                      <select
                        value={operatorServiceId}
                        onChange={(e) => setOperatorServiceId(e.target.value)}
                        disabled={operatorServicesLoading}
                        className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 disabled:opacity-60"
                      >
                        <option value="">{operatorServicesLoading ? 'در حال بارگذاری…' : 'انتخاب کنید'}</option>
                        {operatorServices.filter((s) => s.is_active).map((s) => (
                          <option key={s.id} value={String(s.id)}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => void saveOperatorFields()}
                        disabled={savingOperatorFields}
                        className="shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingOperatorFields ? '…' : 'ذخیره نوع خدمت'}
                      </button>
                    </div>
                  </div>
                )}
                {(() => {
                  const rows =
                    appointment.services && appointment.services.length > 0
                      ? appointment.services
                      : appointment.service
                        ? [appointment.service]
                        : [];
                  return rows.length > 0 ? (
                    <div className="space-y-5">
                      {rows.map((svc, idx) => (
                        <div
                          key={`${svc.name}-${idx}`}
                          className="rounded-xl border border-gray-100 bg-gray-50/60 p-4 sm:p-5"
                        >
                          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                              <dt className="text-sm font-medium text-gray-500">نام خدمت</dt>
                              <dd className="mt-1 text-lg font-bold text-gray-900">{svc.name || 'نامشخص'}</dd>
                            </div>
                            <div className="sm:col-span-2">
                              <dt className="text-sm font-medium text-gray-500 mb-1">توضیحات</dt>
                              <dd className="text-sm text-gray-700 leading-relaxed">
                                {svc.description || 'توضیحاتی ارائه نشده است.'}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
                {!((appointment.services && appointment.services.length > 0) || appointment.service) &&
                appointment.status !== 'pending' ? (
                  <div className="text-center py-12 flex flex-col items-center justify-center text-gray-500">
                    <div className="bg-gray-100 p-4 rounded-full mb-4">
                      <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-lg font-medium">کاربر هنوز خدمتی را انتخاب نکرده است</p>
                    <p className="text-sm text-gray-400 mt-2">انتخاب خدمت در مرحله دوم انجام می‌شود</p>
                  </div>
                ) : null}
              </div>
            )}

            {/* نسخه پزشکی */}
            {activeTab === 'prescription_info' && (
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6 border-b pb-2">
                  نسخه پزشکی و مدارک
                </h3>
                {appointment.step_completed >= 2 ? (
                  <dl className="grid grid-cols-1 gap-6">
                    <div>
                      <dt className="text-sm font-medium text-gray-500 mb-2">کد رهگیری نسخه الکترونیک</dt>
                      <dd className="text-lg font-mono text-gray-900 bg-gray-50 p-3 rounded border border-gray-200 inline-block min-w-[200px] text-center" dir="ltr">
                        {appointment.prescription_tracking_code || '---'}
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500 mb-3">فایل نسخه آپلود شده</dt>
                      <dd className="text-sm text-gray-900">
                        {appointment.prescription_file_path ? (
                          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between max-w-md hover:bg-gray-50 transition-colors">
                            <div className="flex items-center">
                              <svg className="h-10 w-10 text-red-500 ml-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z" />
                              </svg>
                              <div>
                                <p className="font-medium text-gray-900">فایل تصویر نسخه</p>
                                <p className="text-xs text-gray-500">برای مشاهده یا دانلود کلیک کنید</p>
                              </div>
                            </div>
                            <a
                              href={`${getApiBaseUrl().replace('/api', '')}/${appointment.prescription_file_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
                            >
                              مشاهده فایل
                            </a>
                          </div>
                        ) : (
                          <div className="flex items-center text-gray-500 bg-gray-50 p-4 rounded-lg border border-gray-200 max-w-md">
                            <svg className="h-6 w-6 ml-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            فایلی برای نسخه آپلود نشده است
                          </div>
                        )}
                      </dd>
                    </div>
                  </dl>
                ) : appointment.status === 'pending' ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 max-w-xl">
                    <label className="block text-sm font-medium text-gray-700 mb-2">کد رهگیری نسخه الکترونیک</label>
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                      <input
                        type="text"
                        value={operatorTrackingCode}
                        onChange={(e) => setOperatorTrackingCode(e.target.value)}
                        className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-mono text-left"
                        dir="ltr"
                        placeholder="کد رهگیری نسخه"
                      />
                      <button
                        type="button"
                        onClick={() => void saveOperatorFields()}
                        disabled={savingOperatorFields}
                        className="shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingOperatorFields ? '…' : 'ذخیره'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 flex flex-col items-center justify-center text-gray-500">
                    <div className="bg-gray-100 p-4 rounded-full mb-4">
                      <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-lg font-medium">کاربر هنوز اطلاعات نسخه را وارد نکرده است</p>
                    <p className="text-sm text-gray-400 mt-2">تکمیل اطلاعات در مرحله دوم انجام می‌شود</p>
                  </div>
                )}
              </div>
            )}

            {/* فایل‌ها */}
            {activeTab === 'files_info' && (
              <div>
                <div className="flex items-center justify-between mb-6 border-b pb-2">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">مدیریت فایل‌ها</h3>
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('admin_token');
                        const response = await fetch(`${getApiBaseUrl()}/appointments/${id}/send-upload-link`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });

                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const data = await response.json() as any;

                        if (response.ok) {
                          setNotification({ type: 'success', message: 'لینک آپلود با موفقیت پیامک شد' });
                          // Optionally show the link to admin too
                          // alert(`لینک ارسال شد: ${data.upload_url}`);
                        } else {
                          setNotification({ type: 'error', message: data.detail || 'خطا در ارسال پیامک' });
                        }
                      } catch (err) {
                        setNotification({ type: 'error', message: 'خطای شبکه' });
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    ارسال لینک آپلود برای کاربر
                  </button>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        با ارسال لینک آپلود، کاربر پیامکی حاوی یک لینک امن دریافت می‌کند و می‌تواند تصاویر یا مدارک خود را مستقیماً در این پرونده بارگذاری کند.
                      </p>
                    </div>
                  </div>
                </div>

                {/* لیست فایل‌ها */}
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-gray-600">
                      {appointment.upload_token_expires_at ? (
                        (() => {
                          const expiresAt = new Date(appointment.upload_token_expires_at).getTime();
                          const now = new Date().getTime();
                          const distance = expiresAt - now;

                          if (distance < 0) {
                            return <span className="text-red-500 font-medium">لینک آپلود منقضی شده است</span>;
                          } else {
                            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                            const seconds = Math.floor((distance % (1000 * 60)) / 1000);
                            return (
                              <span className="text-blue-600 font-medium flex items-center gap-2">
                                زمان باقی‌مانده برای آپلود:
                                <span className="font-mono text-lg bg-blue-50 px-2 rounded">
                                  {minutes}:{seconds < 10 ? '0' : ''}{seconds}
                                </span>
                              </span>
                            );
                          }
                        })()
                      ) : (
                        <span>لینک آپلود فعال نیست</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Force Refresh Button */}
                      <button
                        onClick={fetchAppointmentDetail}
                        className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                        title="بروزرسانی لیست"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {/* نمایش فایل‌های جدید */}
                  {appointment.files && appointment.files.length > 0 ? (
                    appointment.files.map((file, index) => (
                      <div key={file.id || index} className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="aspect-w-10 aspect-h-7 bg-gray-200 block overflow-hidden h-48">
                          {['jpg', 'jpeg', 'png', 'gif', 'webp'].some(ext => file.file_path?.toLowerCase().endsWith(ext)) ? (
                            <img
                              src={`${getApiBaseUrl().replace('/api', '')}/${file.file_path}`}
                              alt={`فایل ${index + 1}`}
                              className="object-cover w-full h-full"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Broken+Image';
                                (e.target as HTMLImageElement).onerror = null;
                              }}
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full bg-gray-100 text-gray-400">
                              <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="mr-2">سند غیر تصویری</span>
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-xs text-gray-500 mb-2">
                            {new Date(file.created_at || appointment.created_at).toLocaleString('fa-IR')}
                          </p>
                          <a
                            href={`${getApiBaseUrl().replace('/api', '')}/${file.file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full text-center px-4 py-2 border border-blue-500 text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                          >
                            مشاهده / دانلود
                          </a>
                        </div>
                      </div>
                    ))
                  ) : (
                    /* نمایش فایل قدیمی اگر فایل جدیدی نبود */
                    appointment.prescription_file_path && (
                      <div className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="aspect-w-10 aspect-h-7 bg-gray-200 block overflow-hidden h-48">
                          {['jpg', 'jpeg', 'png', 'gif', 'webp'].some(ext => appointment.prescription_file_path?.toLowerCase().endsWith(ext)) ? (
                            <img
                              src={`${getApiBaseUrl().replace('/api', '')}/${appointment.prescription_file_path}`}
                              alt="مدارک پزشکی"
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full bg-gray-100 text-gray-400">
                              <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-xs text-gray-500 mb-2">فایل قدیمی</p>
                          <a
                            href={`${getApiBaseUrl().replace('/api', '')}/${appointment.prescription_file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full text-center px-4 py-2 border border-blue-500 text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100"
                          >
                            مشاهده
                          </a>
                        </div>
                      </div>
                    )
                  )}

                  {(!appointment.files || appointment.files.length === 0) && !appointment.prescription_file_path && (
                    <div className="col-span-full text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">هیچ فایلی موجود نیست</h3>
                      <p className="mt-1 text-sm text-gray-500">برای دریافت مدارک، لینک آپلود را ارسال کنید.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* پاپ‌آپ تعیین زمان */}
        {showTimeModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
            <div className="relative p-5 border w-96 shadow-lg rounded-md bg-white mx-auto">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
                  تکمیل نوبت - تنظیم زمان مراجعه
                </h3>

                {appointment && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600">
                      <strong>نام:</strong> {appointment.user?.full_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>خدمت:</strong> {appointment.service?.name}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    تاریخ مراجعه (شمسی)
                  </label>
                  <PersianDatePicker
                    value={appointmentDate}
                    onChange={setAppointmentDate}
                    placeholder="تاریخ مراجعه را انتخاب کنید"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ساعت مراجعه
                  </label>
                  <TimePicker
                    value={appointmentTime}
                    onChange={setAppointmentTime}
                    placeholder="ساعت را انتخاب کنید"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={betweenPatients}
                      onChange={(e) => setBetweenPatients(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-800">بین مریض</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 mr-6">
                    در صورت فعال بودن، به پیامک زمان نوبت جملهٔ «دو ساعت زمان انتظار در مرکز…» اضافه می‌شود.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sendCompleteSms}
                      onChange={(e) => setSendCompleteSms(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-800">ارسال پیامک تکمیل نوبت</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 mr-6">
                    در صورت غیرفعال کردن، پیامک زمان نوبت برای بیمار ارسال نمی‌شود.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    توضیحات (اختیاری)
                  </label>
                  <textarea
                    value={appointmentNotes}
                    onChange={(e) => setAppointmentNotes(e.target.value)}
                    placeholder="توضیحات اضافی در مورد نوبت..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeTimeModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    لغو
                  </button>
                  <button
                    onClick={saveAppointmentTime}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    تکمیل نوبت
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* پاپ‌آپ لغو نوبت */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
            <div className="relative p-5 border w-96 shadow-lg rounded-md bg-white mx-auto">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
                  لغو نوبت
                </h3>

                {appointment && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600">
                      <strong>نام:</strong> {appointment.user?.full_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>خدمت:</strong> {appointment.service?.name}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    دلیل لغو نوبت *
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="دلیل لغو نوبت را وارد کنید..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows={4}
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={sendCancelSms}
                      onChange={(e) => setSendCancelSms(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    ارسال پیامک لغو برای کاربر
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeCancelModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    انصراف
                  </button>
                  <button
                    onClick={cancelAppointment}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    لغو نوبت
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div >
  );
};

export default AdminAppointmentDetail;
